/**
 * warmWorkspace — fire-and-forget ORG VM pre-provisioning.
 *
 * D1 (2026-08-24 owner directive): workstations bind to ORGANIZATIONS only —
 * personal (uid-plane) workspaces run serverless and never mint machines, so
 * the old ambient warmWorkspaceVm() (empty-body provisionUserVM against the
 * caller's own plane, 2026-08-12 era) is deleted. What remains is the
 * draft-org warm: reserve the organization's accountId the moment the user
 * arrives at onboarding, then kick the idempotent provision against that org
 * id so the workstation is ready by the time it's needed.
 *
 * Strictly best-effort by design: every failure (network, refused provision,
 * already provisioning) is swallowed after a console note — the connect
 * flow's own boot gate remains the safety net.
 */

import { getAuth } from 'firebase/auth';

const DRAFT_ORG_LATCH_KEY = 'prest0_draft_org_warm';

function fnUrl(name: string): string {
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
  return `https://us-central1-${projectId}.cloudfunctions.net/${name}`;
}

// Concurrent-call dedupe for the draft-org path (mount + resume effects).
let draftWarmInFlight: Promise<string | null> | null = null;

/**
 * Draft-org warm (owner directive 2026-08-23): the ORGANIZATION'S VM starts
 * the moment the account exists — long before the legal-name confirm mints
 * the org docs — so the workstation (and the in-onboarding browser sign-in
 * that runs on it) is ready by the time the user asks for it.
 *
 * Mechanics: reserve the org's accountId via createOrganizationWorkspace's
 * reserve-only mode (Gate-checked, idempotent, pins the id on the caller's
 * pending_org draft), then kick the idempotent provisionUserVM against it.
 * The later full mint reuses the same id, so this VM IS the org's VM. Best
 * effort throughout: failures fall back to the profile-save mint's own warm.
 * Returns the reserved accountId (cached per session) or null.
 */
export function reserveAndWarmDraftOrgVm(): Promise<string | null> {
  const uid = getAuth().currentUser?.uid ?? null;
  try {
    const cached = readLatch(uid);
    if (cached) return Promise.resolve(cached);
  } catch { /* storage unavailable — attempt once per module load */ }
  if (draftWarmInFlight) return draftWarmInFlight;

  draftWarmInFlight = (async (): Promise<string | null> => {
    try {
      const user = getAuth().currentUser;
      if (!user || user.isAnonymous) return null;

      // Gate 2 of the reserve needs the draft doc to exist; a merge write is
      // a no-op on an already-started draft.
      const { getFirestore, doc, setDoc, serverTimestamp } = await import('firebase/firestore');
      await setDoc(doc(getFirestore(), 'users', user.uid, 'onboarding', 'pending_org'), {
        draftStartedAt: serverTimestamp(),
      }, { merge: true });

      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const reserve = httpsCallable(getFunctions(undefined, 'us-central1'), 'createOrganizationWorkspace');
      const result = await reserve({ reserveOnly: true });
      const data = (result?.data ?? {}) as { reservedAccountId?: string; accountId?: string };
      // A draft that already fully minted returns accountId — same warm target.
      const orgId = data.reservedAccountId || data.accountId || null;
      if (!orgId) return null;

      const token = await user.getIdToken();
      const resp = await fetch(fnUrl('provisionUserVM'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId: orgId }),
      });
      if (!resp.ok) {
        console.log(`[warmWorkspace] draft-org warm skipped (HTTP ${resp.status})`);
        // The reservation stands even when the provision was refused — the
        // profile-save mint retries the warm; cache the id either way.
      }
      try {
        sessionStorage.setItem(DRAFT_ORG_LATCH_KEY, JSON.stringify({ uid: user.uid, orgId }));
      } catch { /* noop */ }
      console.log('[warmWorkspace] draft-org VM warming', { orgId, provisioned: resp.ok });
      return orgId;
    } catch (err) {
      console.log('[warmWorkspace] draft-org warm failed (non-fatal):', err);
      return null;
    } finally {
      draftWarmInFlight = null;
    }
  })();
  return draftWarmInFlight;
}

/**
 * Read the latch, but ONLY for the signed-in uid (review 2026-08-23): the
 * latch is session-scoped, so a shared tab — or the same person starting a
 * SECOND organization after finishing the first — would otherwise inherit
 * the previous org's id and point that org's connects at the wrong VM.
 */
function readLatch(uid: string | null): string | null {
  try {
    const raw = sessionStorage.getItem(DRAFT_ORG_LATCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { uid?: string; orgId?: string };
    if (!parsed?.orgId) return null;
    if (uid && parsed.uid && parsed.uid !== uid) return null;
    return parsed.orgId;
  } catch {
    return null;
  }
}

/** The session's reserved draft-org id, when the warm has run for this user. */
export function getReservedDraftOrgId(): string | null {
  return readLatch(getAuth().currentUser?.uid ?? null);
}

/**
 * Drop the reservation latch — called when a draft is CONSUMED (its mint
 * landed and the flow reset it) or when onboarding completes, so a later
 * "create another organization" in the same tab reserves a fresh id instead
 * of resuming the finished org's (review 2026-08-23).
 */
export function clearReservedDraftOrgId(): void {
  try { sessionStorage.removeItem(DRAFT_ORG_LATCH_KEY); } catch { /* noop */ }
  draftWarmInFlight = null;
}
